// Thin axios wrappers around the Phase 1b super-admin licensing CRUD.
// All 12 routes live under `/api/admin/licensing/...`, which is the
// base URL configured on adminAxios.
//
// Spec: admin/server/docs/LICENSING_V2.md + commit 1a56ad91 on
// feat/licensing-v2 in layerd-controlplane-backend.

import api from "../utils/axios/v1/adminAxios";

// ---------------- Reads ----------------

export const getSkus = () => api.get("/licensing/skus");

export const getOrgs = () => api.get("/licensing/orgs");

export const getOrg = (orgId) =>
  api.get(`/licensing/orgs/${encodeURIComponent(orgId)}`);

export const getOrgUsage = (orgId, { range = "30d" } = {}) =>
  api.get(`/licensing/orgs/${encodeURIComponent(orgId)}/usage`, {
    params: { range },
  });

export const getBreaches = () => api.get("/licensing/breaches");

export const getFeatures = (skuId) =>
  api.get("/licensing/features", { params: { sku_id: skuId } });

// Per-org effective feature view: catalog rows enriched with current_value,
// is_overridden, source. Powers the inline FeatureTable on Detail.js.
// Shipped CP commit 3460d099 on feat/licensing-v2.
export const getOrgSkuFeatures = (orgId, skuId) =>
  api.get(
    `/licensing/orgs/${encodeURIComponent(orgId)}/skus/${encodeURIComponent(skuId)}/features`,
  );

// ---------------- Writes ----------------

export const grantSku = (orgId, body) =>
  api.post(`/licensing/orgs/${encodeURIComponent(orgId)}/skus`, body);

export const updateSku = (orgId, skuId, body) =>
  api.patch(
    `/licensing/orgs/${encodeURIComponent(orgId)}/skus/${encodeURIComponent(skuId)}`,
    body,
  );

export const revokeSku = (orgId, skuId) =>
  api.delete(
    `/licensing/orgs/${encodeURIComponent(orgId)}/skus/${encodeURIComponent(skuId)}`,
  );

export const setFeatureOverride = (orgId, skuId, featureKey, value) =>
  api.put(
    `/licensing/orgs/${encodeURIComponent(orgId)}/skus/${encodeURIComponent(skuId)}/features/${encodeURIComponent(featureKey)}`,
    { value },
  );

export const clearFeatureOverride = (orgId, skuId, featureKey) =>
  api.delete(
    `/licensing/orgs/${encodeURIComponent(orgId)}/skus/${encodeURIComponent(skuId)}/features/${encodeURIComponent(featureKey)}`,
  );

// Platform-feature override path (features.sku_id IS NULL in catalog).
// CP auto-routes the override row under any held SKU; caller doesn't need
// to know which one. 403 if the org has no active SKUs.
// Shipped CP commit 56e86602 on feat/licensing-v2.
export const setPlatformFeatureOverride = (orgId, featureKey, value) =>
  api.put(
    `/licensing/orgs/${encodeURIComponent(orgId)}/platform/features/${encodeURIComponent(featureKey)}`,
    { value },
  );

export const clearPlatformFeatureOverride = (orgId, featureKey) =>
  api.delete(
    `/licensing/orgs/${encodeURIComponent(orgId)}/platform/features/${encodeURIComponent(featureKey)}`,
  );

export const resolveBreach = (breachId, resolutionState) =>
  api.post(`/licensing/breaches/${encodeURIComponent(breachId)}/resolve`, {
    resolution_state: resolutionState,
  });

export const patchOrgLimits = (orgId, body) =>
  api.patch(
    `/licensing/orgs/${encodeURIComponent(orgId)}/limits`,
    body,
  );

const licensingService = {
  getSkus,
  getOrgs,
  getOrg,
  getOrgUsage,
  getBreaches,
  getFeatures,
  getOrgSkuFeatures,
  grantSku,
  updateSku,
  revokeSku,
  setFeatureOverride,
  clearFeatureOverride,
  setPlatformFeatureOverride,
  clearPlatformFeatureOverride,
  resolveBreach,
  patchOrgLimits,
};

export default licensingService;
